import {
  useEffect,
  useRef,
  useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";
import * as amplitude from '@amplitude/analytics-browser';
import {
  useMutation,
} from "@tanstack/react-query";

import {
  Badge,
  Drawer,
} from "@unified-checkout/design-system";

import {
  ChartLine,
  CircleDot,
  CircleExclamation,
  CircleInfo,
  CircleSmall,
  ArrowUpRight,
  Crown,
  Heart,
  PaperPlane,
  User,
} from "@unified-checkout/design-system/icons/index";

import { COOKIES_KEYS } from '@app/dashboard/constants/cookies-keys';

import type { IPreDefinedRange } from "libs/design-system/src/types/date_range_picker";
import DateRanges from "@app/dashboard/components/inner_layout/DateRanges";
import type { TAppliedRanges } from "@app/dashboard/types/innerLayout/date-ranges";
import { mainPreDefinedRanges } from "@app/dashboard/utils/dateRangePicker";
import {
  getFromLocalStorage,
  addToLocalStorage
} from "@app/dashboard/utils/manageLocalStorage";
import { notify } from "@app/dashboard/utils/notifier";
import {
  getLang,
  getMerchantToken
} from "@app/dashboard/utils/localStorageUtils";
import { getRefreshToken } from '@app/dashboard/utils/manageLocalStorage';
import { setCrossSubdomainsCookie } from '@app/dashboard/utils/manageCookies';

import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import {
  PAYMOB_PRO_EVENTS,
  PAYMOB_PRO_EVENTS_PARAMS,
  PAYMOB_PRO_VALUES
} from "@app/dashboard/constants/tracking_events/paymob-pro";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import {
  PAYMOB_PRO_URL,
  SUBSCRIBED,
  TRIAL_PLAN,
  UNSUBSCRIBED
} from "@app/dashboard/constants/paymob-pro";
import {
  showPaymobProDrawer,
  startFreeTrial
} from "@app/dashboard/services/inner_layout/paymob-pro";
import type { IPaymobProTrialDates } from "@app/dashboard/types/innerLayout/paymob_pro/paymob-pro";
import type { IMetrics } from "@app/dashboard/types/innerLayout/metrics";

import { Metrics } from "../Metrics";
import { MultiSectionCard } from "../MultiSectionCard";
import { TrialConfirmationModal } from "./TrialConfirmationModal/TrialConfirmationModal";
import { PricingDrawer } from "./PricingDrawer/PricingDrawer";

import enGraph from "./assets/en-graph.svg";
import arGraph from './assets/ar-graph.svg'

import {
  getBtnText,
  getPaymobProDaysLeft,
  getPaymobProStatus,
  isBadgeVisible
} from "./utils";
import styles from "./paymob-pro.module.scss";

export const PaymobPro = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 900;
  const lang = getLang();
  const dateRangeContainerRef = useRef<HTMLDivElement>(null);

  const defaultPreDefinedRange: IPreDefinedRange | undefined = mainPreDefinedRanges(
    t
  )?.find((range) => range.default);

  const initialAppliedRanges: TAppliedRanges = {
    index: defaultPreDefinedRange?.index,
    name: defaultPreDefinedRange?.name,
    start: defaultPreDefinedRange?.selectionRange[0],
    end: defaultPreDefinedRange?.selectionRange[1],
  };
  const isPaymobProActive = getFromLocalStorage(LOCAL_STORAGE_KEYS.PAYMOB_PLAN) === TRIAL_PLAN;
  const shouldOpenFromLogin = getFromLocalStorage(LOCAL_STORAGE_KEYS.OPEN_PAYMOB_PRO_DRAWER) && !isMobile;
  const shouldOpenFromDeepLink = location.state?.openPaymobPro === true && !isMobile;
  const storedPaymobProEndDate = getFromLocalStorage(LOCAL_STORAGE_KEYS.PAYMOB_PRO_END_DATE)

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isPaymobProDrawerOpen, setIsPaymobProDrawerOpen] = useState(shouldOpenFromLogin);
  const [isPricingDrawerOpen, setIsPricingDrawerOpen] = useState(false);
  const [isPaymobProActived, setIsPaymobProActived] = useState(isPaymobProActive);
  const [appliedRanges, setAppliedRanges] = useState(initialAppliedRanges);
  const [trialDates, setTrialDates] = useState<IPaymobProTrialDates | null>(null);
  const [paymobProEndDate, setPaymobProEndDate] = useState<string | null>(storedPaymobProEndDate);

  const paymobProDrawerOpenRef = useRef(isPaymobProDrawerOpen);

  const handleFreeTrialSuccess = ({ data }: { data: IPaymobProTrialDates }) => {
    setTrialDates({
      start_date: data.start_date,
      end_date: data.end_date,
    });
    setIsConfirmationModalOpen(true);
    setIsPaymobProActived(true);
    setIsPaymobProDrawerOpen(false);
    addToLocalStorage(LOCAL_STORAGE_KEYS.PAYMOB_PLAN, TRIAL_PLAN);
    addToLocalStorage(LOCAL_STORAGE_KEYS.PAYMOB_PRO_END_DATE, data.end_date);
    setPaymobProEndDate(data.end_date);
    amplitude.track(PAYMOB_PRO_EVENTS.TRIAL_STARTED, {
      [PAYMOB_PRO_EVENTS_PARAMS.TRIAL_START_AT]: data.start_date,
      [PAYMOB_PRO_EVENTS_PARAMS.TRIAL_END_AT]: data.end_date,
      [PAYMOB_PRO_EVENTS_PARAMS.MONTHLY_FEE]: "0",
      [PAYMOB_PRO_EVENTS_PARAMS.CURRENCY]: "AED",
    });
  };

  const handleFreeTrialError = (error: any) => {
    notify.error(error?.response?.data?.message || t('errors.somethingWentWrong'));

    amplitude.track(PAYMOB_PRO_EVENTS.TRIAL_START_FAILED, {
      [PAYMOB_PRO_EVENTS_PARAMS.ERROR_CODE]: error?.response?.data?.error_code || PAYMOB_PRO_VALUES.ERROR_MESSAGE,
    });
  };

  const {
    isPending: isFreeTrialLoading,
    mutate: freeTrialMutate,
  } = useMutation({
    mutationKey: [QUERY_KEYS.GET_PAYMOB_PRO_FREE_TRIAL],
    mutationFn: () => startFreeTrial(),
    onSuccess: handleFreeTrialSuccess,
    onError: handleFreeTrialError,
  })

  const { mutate: showPaymobDrawer } = useMutation({
    mutationKey: [QUERY_KEYS.SHOW_PAYMOB_PRO_DRAWER],
    mutationFn: () => showPaymobProDrawer(),
    onSuccess: () => {
      addToLocalStorage(LOCAL_STORAGE_KEYS.OPEN_PAYMOB_PRO_DRAWER, false);
    }
  })

  const handleRangesChange = (ranges: TAppliedRanges) => {
    setAppliedRanges(ranges);
  };

  const localizedPaymobCards = t('innerLayout.paymobPro.paymobCards', { returnObjects: true }) as Array<{
    title: string;
    subtitle: string;
  }>;
  const paymobCard = localizedPaymobCards.map((card, index) => ({
    ...card,
    icon: [ChartLine, User, PaperPlane][index],
    color: ['gray', 'blue', 'violet'][index] as 'gray' | 'blue' | 'violet'
  }));

  const metricCardsData = t('innerLayout.paymobPro.metricCards', { returnObjects: true }) as IMetrics['data'];

  const localizedBadgeItems = t('innerLayout.paymobPro.badgeItems', { returnObjects: true }) as Array<{
    label: string;
    count: string;
  }>;
  const badgeItems = localizedBadgeItems.map((badge, index) => ({
    ...badge,
    size: 'sm',
    leadingIcon: [CircleDot, Heart, CircleExclamation][index],
    color: ['primary', 'green', 'red'][index] as 'primary' | 'green' | 'red'
  }));

  const paymobProStatus = getPaymobProStatus(paymobProEndDate);
  const isPaymobProSubscribed = paymobProStatus === SUBSCRIBED;
  const isPaymobProUnsubscribed = paymobProStatus === UNSUBSCRIBED;

  const handleCloseDrawer = () => {
    setIsPaymobProDrawerOpen(false)
  };

  const handleFreeTrialClick = () => {
    freeTrialMutate()
    amplitude.track(PAYMOB_PRO_EVENTS.TRIAL_CTA_CLICKED);
  };

  const addAccessTokenToCookie = () => {
    const accessToken = getMerchantToken();
    const refreshToken = getRefreshToken();
    const host = window.location.hostname;

    const domain = host === 'localhost' ? 'localhost' : '.paymob.com';

    accessToken && setCrossSubdomainsCookie({
      name: COOKIES_KEYS.PAYMOB_PRO_TOKEN,
      value: accessToken,
      domain,
    });

    refreshToken && setCrossSubdomainsCookie({
      name: COOKIES_KEYS.PAYMOB_PRO_REFRESH_TOKEN,
      value: refreshToken,
      domain,
    });
  }

  const handlePaymobProClick = () => {
    if (isPaymobProActived) {
      addAccessTokenToCookie();

      window.open(PAYMOB_PRO_URL, '_blank');
    } else {
      setIsPaymobProDrawerOpen(true);
    }

  }

  useEffect(() => {
    if (shouldOpenFromDeepLink) {
      handlePaymobProClick();
      
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
  }, [shouldOpenFromDeepLink]);

  useEffect(() => {
    if (isPaymobProDrawerOpen) {
      amplitude.track(PAYMOB_PRO_EVENTS.LAUNCH_DRAWER_VIEWED);
    }

    if (paymobProDrawerOpenRef.current && !isPaymobProDrawerOpen) {
      amplitude.track(PAYMOB_PRO_EVENTS.LAUNCH_DRAWER_DISMISSED);
      if (shouldOpenFromLogin) {
        showPaymobDrawer();
      }
    }

    paymobProDrawerOpenRef.current = isPaymobProDrawerOpen;
  }, [isPaymobProDrawerOpen]);

  const Header =
    <div className={styles['paymob-pro-drawer__header']}>
      <Badge color="primary" text={t('innerLayout.paymobPro.headerBadge')} size="md" />
      <div className={styles['paymob-pro-drawer__header__title']}>
        {t('innerLayout.paymobPro.drawerTitle')}
      </div>
    </div>

  const MetricsCards =
    <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper']}>
      <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__header']}>
        <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__header__title']}>
          {t('innerLayout.paymobPro.cardsHeader')}
        </div>
        <div
          className={styles['paymob-pro-drawer__body__insight-cards__wrapper__header__button-date-range']}
          ref={dateRangeContainerRef}
        >
          <DateRanges
            appliedRanges={appliedRanges}
            disabled={true}
            handleRangesChange={handleRangesChange}
            lang={lang}
            containerRef={dateRangeContainerRef as any}
          />
        </div>
      </div>
      <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__body']}>
        <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__body__chart-card']}>
          <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__body__chart-card__title']}>
            {t('innerLayout.paymobPro.cardsBodyTitle')}
          </div>
          <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__body__chart-card__chart']}>
            <img src={lang === 'ar' ? arGraph : enGraph} alt="chart" />
          </div>
        </div>
        <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__body__metrics']}>
          <Metrics
            data={metricCardsData}
            //isLoading={isPaymobProDataLoading}
            noOfCards={2}
            comparisonRangeName={t('innerLayout.homePage.overview.dateRangePicker.preDefinedRangesLabels.lastWeek')}
          />
        </div>
      </div>
      <div className={styles['paymob-pro-drawer__body__insight-cards__wrapper__description']}>
        {CircleInfo}
        {t('innerLayout.paymobPro.cardsDescription')}
      </div>
    </div>

  const SegmentsBadges =
    <div className={styles['paymob-pro-drawer__body__segments']}>
      <div className={styles['paymob-pro-drawer__body__segments__title']}>
        {t('innerLayout.paymobPro.segmentsTitle')}
      </div>
      <div className={styles['paymob-pro-drawer__body__segments__badges']}>
        {badgeItems.map((badge, index) => (
          <div key={index}>
            <Badge
              size="sm"
              leadingIcon={badge.leadingIcon}
              color={badge.color}
              text={
                <div className={styles['paymob-pro-drawer__body__segments__badges__badge-text']}>
                  <span>{badge.label}</span>
                  <span>{badge.count}</span>
                </div>
              }
            />
          </div>
        ))}
      </div>
    </div>

  const MultiSectionCards =
    <div className={styles['paymob-pro-drawer__body__multi-section-cards']}>
      {paymobCard.map((card, index) => (
        <div key={index}>
          <MultiSectionCard
            title={card.title}
            subtitle={card.subtitle}
            icon={card.icon}
            color={card.color}
          />
        </div>
      ))}
    </div>

  const PaymobProBtn = () => {
    const paymobProDaysLeft = getPaymobProDaysLeft(paymobProEndDate);

    const getPaymobProBtnText = isPaymobProSubscribed ?
      t('innerLayout.navbar.paymobProPendingStatus', { days: paymobProDaysLeft })
      :
      t('innerLayout.navbar.paymobPro');

    const paymobProIconLeadingIcon = isPaymobProSubscribed ? CircleSmall : Crown;

    const paymobProTrailingIcon =
      isBadgeVisible(screenWidth) &&
      (
        isPaymobProUnsubscribed ?
          <div className={styles['paymob-pro-btn__badge']}>
            {t('innerLayout.navbar.new')}
          </div>
          : isPaymobProSubscribed ?
            ArrowUpRight
            : null
      );

    return (
      <div
        className={styles['paymob-pro-btn']}
        onClick={handlePaymobProClick}
        data-paymob-pro-status={paymobProStatus}
      >
        <div className={styles['paymob-pro-btn__icon']}>
          {paymobProIconLeadingIcon}
        </div>
        {getBtnText(getPaymobProBtnText, screenWidth)}
        {paymobProTrailingIcon}
      </div>
    )
  }

  const PaymobProDrawer = () => {
    const btnsProps = [
      {
        text: t('innerLayout.paymobPro.drawerBtn1'),
        variant: "contained" as const,
        color: "primary" as const,
        onClickHandler: handleFreeTrialClick,
        loading: isFreeTrialLoading,
        size: "md" as const
      },
      {
        text: t('innerLayout.paymobPro.drawerBtn2'),
        variant: "link" as const,
        color: "primary" as const,
        onClickHandler: () => {
          setIsPricingDrawerOpen(true);
        },
        size: "md" as const
      }
    ]

    return (
      <Drawer
        open={isPaymobProDrawerOpen}
        wrapperClassName={styles['paymob-pro-drawer']}
        closeOnClickOutside={false}
        onCloseHandler={handleCloseDrawer}
        title={Header}
        supportingText={t('innerLayout.paymobPro.drawerSupportingText')}
        hasCloseIcon
        isOverlayAboveModal
        position="inline-end"
        btnsProps={btnsProps}
        footerHint={t('innerLayout.paymobPro.staticCardsFooter')}
      >
        <div className={styles['paymob-pro-drawer']}>
          <div className={styles['paymob-pro-drawer__body']}>
            <div className={styles['paymob-pro-drawer__body__insight-cards']}>
              {MetricsCards}
              {SegmentsBadges}
            </div>
            {MultiSectionCards}
          </div>
        </div>
      </Drawer>
    )
  };

  return (
    <>
      {PaymobProBtn()}
      {PaymobProDrawer()}

      <TrialConfirmationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        setIsPaymobProDrawerOpen={setIsPaymobProDrawerOpen}
        onAdvisorClickHandler={addAccessTokenToCookie}
        trialDates={trialDates}
      />

      <PricingDrawer
        isPricingDrawerOpen={isPricingDrawerOpen}
        setIsPricingDrawerOpen={setIsPricingDrawerOpen}
        setIsPaymobProDrawerOpen={setIsPaymobProDrawerOpen}
      />
    </>
  );
};
